import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Plus, Home, Trash2, Package, Camera, AlertTriangle } from 'lucide-react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { SkeletonList } from '../components/ui/Skeleton';
import { SwipeableRow } from '../components/ui/SwipeableRow';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { MetricPill } from '../components/ui/MetricPill';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchBar } from '../components/ui/SearchBar';
import { PageShell } from '../components/ui/PageShell';
import { Room, Project, Item, Photo } from '../types';
import { getProject } from '../services/projectService';
import { getRoomsByProject, deleteRoom } from '../services/roomService';
import { getItemsByProject, getItemCountsByRooms, deleteItem } from '../services/itemService';
import { getPhotosByProject } from '../services/photoService';
import { getDamageReportsByProject } from '../services/damageReportService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import ConfirmDialog from '../components/ConfirmDialog';

const VIEW_SEGMENTS = [
  { value: 'rum', label: 'Rum' },
  { value: 'objekt', label: 'Alla objekt' },
];

const RoomListPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [project, setProject] = useState<Project | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});
  const [photoCounts, setPhotoCounts] = useState<Record<string, number>>({});
  const [damageCounts, setDamageCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'room' | 'item'>('room');
  const [activeView, setActiveView] = useState('rum');
  const [searchTerm, setSearchTerm] = useState('');
  const [photoLoadError, setPhotoLoadError] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);

        const [fetchedProject, fetchedRooms, fetchedItemCounts, fetchedItems, fetchedPhotos, fetchedDamageReports] = await Promise.all([
          getProject(projectId),
          getRoomsByProject(projectId),
          getItemCountsByRooms(projectId),
          getItemsByProject(projectId),
          getPhotosByProject(projectId),
          getDamageReportsByProject(projectId),
        ]);

        setProject(fetchedProject);
        setRooms(fetchedRooms);
        setItemCounts(fetchedItemCounts);
        setItems(fetchedItems);
        setPhotos(fetchedPhotos);

        // Compute photo counts per room
        const pCounts: Record<string, number> = {};
        fetchedPhotos.forEach(p => {
          if (p.roomId) {
            pCounts[p.roomId] = (pCounts[p.roomId] || 0) + 1;
          }
        });
        setPhotoCounts(pCounts);

        // Compute damage counts per room (via items)
        const dCounts: Record<string, number> = {};
        fetchedDamageReports.forEach(r => {
          if (r.itemId) {
            const item = fetchedItems.find(i => i.id === r.itemId);
            if (item?.roomId) {
              dCounts[item.roomId] = (dCounts[item.roomId] || 0) + 1;
            }
          }
        });
        setDamageCounts(dCounts);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte hämta data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Room delete
  const handleDeleteRoom = (roomId: string) => {
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att ta bort rum.');
      return;
    }
    setDeleteType('room');
    setDeleteConfirmId(roomId);
  };

  const confirmDeleteRoom = async () => {
    const roomId = deleteConfirmId;
    if (!roomId || !isOnline) return;
    setDeleteConfirmId(null);
    try {
      await deleteRoom(roomId, projectId);
      setRooms(rooms.filter(r => r.id !== roomId));
    } catch (err) {
      console.error('Error deleting room:', err);
      setError('Kunde inte ta bort rummet.');
    }
  };

  // Item delete (for "Alla objekt" view)
  const handleDeleteItem = (itemId: string) => {
    if (!isOnline) {
      setError('Du är offline.');
      return;
    }
    setDeleteType('item');
    setDeleteConfirmId(itemId);
  };

  const confirmDeleteItem = async () => {
    const itemId = deleteConfirmId;
    if (!itemId || !projectId) return;
    setDeleteConfirmId(null);
    try {
      await deleteItem(itemId, projectId);
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Kunde inte ta bort objektet.');
    }
  };

  // Room name lookup
  const getRoomName = (roomId: string | undefined) => {
    if (!roomId) return 'Utan rum';
    const room = rooms.find(r => r.id === roomId);
    return room?.name || 'Okänt rum';
  };

  // Get item photos for thumbnails
  const getItemPhotos = (item: Item) => {
    return photos.filter(p => p.itemId === item.id);
  };

  // Filtered items for "Alla objekt" segment
  const filteredItems = items.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return item.name.toLowerCase().includes(s) ||
      item.description.toLowerCase().includes(s) ||
      item.category.toLowerCase().includes(s);
  });

  if (loading) {
    return (
      <PageShell title="Rum & Objekt" backTo={projectId ? `/projects/${projectId}` : '/'} backLabel="Projekt">
        <div className="space-y-4 py-2">
          <SkeletonList count={4} />
        </div>
      </PageShell>
    );
  }

  if (error && !project) {
    return (
      <PageShell title="Rum & Objekt" backTo={projectId ? `/projects/${projectId}` : '/'} backLabel="Projekt">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          {error || 'Projektet kunde inte hittas.'}
        </div>
      </PageShell>
    );
  }

  if (!project) return null;

  return (
    <PageShell
      title="Rum & Objekt"
      backTo={`/projects/${projectId}`}
      backLabel="Projekt"
      headerRight={
        activeView === 'rum' ? (
          <button
            onClick={() => navigate(`/projects/${projectId}/rooms/new`)}
            className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity"
          >
            Nytt rum
          </button>
        ) : (
          <button
            onClick={() => navigate(`/projects/${projectId}/items/new`)}
            className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity"
          >
            Nytt objekt
          </button>
        )
      }
    >
      <div className="pb-24">
        {/* Segmented Control */}
        <div className="flex justify-center px-4 py-3">
          <SegmentedControl
            segments={VIEW_SEGMENTS}
            value={activeView}
            onChange={(v) => { setActiveView(v); setSearchTerm(''); }}
            className="w-full"
          />
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* ===== RUM VIEW ===== */}
        {activeView === 'rum' && (
          <div className="px-4 space-y-2">
            {rooms.length === 0 ? (
              <EmptyState
                icon={Home}
                title="Inga rum"
                description={
                  photos.length > 0 || items.length > 0
                    ? `Projektet har ${photos.length} foto${photos.length === 1 ? '' : 'n'}${items.length > 0 ? ` och ${items.length} objekt` : ''} men inga rum än. Lägg till rum för att organisera dem.`
                    : "Lägg till rum för att börja dokumentera."
                }
                action={
                  <Link
                    to={`/projects/${projectId}/rooms/new`}
                    className="inline-flex items-center gap-2 bg-accent-600 text-white px-4 py-2.5 text-sm font-medium rounded-xl active:bg-accent-700 transition-colors"
                  >
                    <Plus size={16} />
                    Lägg till rum
                  </Link>
                }
              />
            ) : (
              <div className="space-y-2">
                {rooms.map((room) => (
                  <SwipeableRow
                    key={room.id}
                    className="rounded-ios-card border border-[var(--border-default)]/80 overflow-hidden"
                    rightActions={[
                      {
                        label: 'Ta bort',
                        icon: <Trash2 size={18} />,
                        color: '#ef4444',
                        onClick: () => handleDeleteRoom(room.id),
                      },
                    ]}
                  >
                    <Link
                      to={`/projects/${projectId}/rooms/${room.id}`}
                      className="block bg-[var(--bg-surface)] press-row"
                    >
                      <div className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Home size={18} className="text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-[var(--text-primary)] text-[15px] truncate">{room.name}</h3>
                            {room.notes && (
                              <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-1">{room.notes}</p>
                            )}
                          </div>
                        </div>

                        {/* Metric pills */}
                        <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-[var(--border-subtle)]">
                          <MetricPill icon={Package} count={itemCounts[room.id] || 0} />
                          <MetricPill icon={Camera} count={photoCounts[room.id] || 0} />
                          <MetricPill icon={AlertTriangle} count={damageCounts[room.id] || 0} />
                          {!itemCounts[room.id] && !photoCounts[room.id] && !damageCounts[room.id] && (
                            <span className="text-xs text-[var(--text-tertiary)]">Tomt rum</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </SwipeableRow>
                ))}

                {/* Unassigned items card */}
                {itemCounts['unassigned'] > 0 && (
                  <div className="bg-[var(--bg-surface)] rounded-ios-card border border-dashed border-[var(--border-default)] p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package size={18} className="text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[var(--text-primary)] text-[15px]">Utan rum</h3>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                          {itemCounts['unassigned']} objekt utan tilldelat rum
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== ALLA OBJEKT VIEW ===== */}
        {activeView === 'objekt' && (
          <div className="px-4 space-y-2">
            {items.length > 3 && (
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Sök objekt..."
              />
            )}

            {filteredItems.length === 0 ? (
              <EmptyState
                icon={Package}
                title="Inga objekt"
                description={searchTerm ? 'Inga objekt matchar sökningen.' : 'Lägg till objekt via ett rum.'}
              />
            ) : (
              <div className="space-y-2">
                {filteredItems.map(item => {
                  const itemPhotos = getItemPhotos(item);
                  const hasPhoto = itemPhotos.length > 0 && !photoLoadError[itemPhotos[0]?.id];

                  return (
                    <SwipeableRow
                      key={item.id}
                      className="rounded-ios-card border border-[var(--border-default)]/80 overflow-hidden"
                      rightActions={[
                        {
                          label: 'Ta bort',
                          icon: <Trash2 size={18} />,
                          color: '#ef4444',
                          onClick: () => handleDeleteItem(item.id),
                        },
                      ]}
                    >
                      <Link
                        to={`/projects/${projectId}/items/${item.id}`}
                        className="block bg-[var(--bg-surface)] press-row"
                      >
                        <div className="p-3">
                          <div className="flex gap-3">
                            <div className="flex-shrink-0">
                              {hasPhoto ? (
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--bg-surface-alt)]">
                                  <img
                                    src={itemPhotos[0].url}
                                    loading="lazy"
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={() => setPhotoLoadError(prev => ({ ...prev, [itemPhotos[0].id]: true }))}
                                  />
                                </div>
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-[var(--bg-surface-alt)] flex items-center justify-center">
                                  <Package size={16} className="text-[var(--text-tertiary)]" />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                  <h3 className="font-semibold text-[var(--text-primary)] text-sm truncate">{item.name}</h3>
                                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                    <Home size={11} className="inline mr-1 text-[var(--text-tertiary)]" />
                                    {getRoomName(item.roomId)}
                                    {item.category && <> · {item.category}</>}
                                  </p>
                                </div>
                                <StatusBadge status={item.priority} size="sm" className="ml-2 flex-shrink-0" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </SwipeableRow>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteConfirmId !== null}
        title="Ta bort"
        message={deleteType === 'room'
          ? 'Är du säker på att du vill ta bort detta rum? Alla objekt och foton i rummet tas också bort.'
          : 'Är du säker på att du vill ta bort detta objekt? Denna åtgärd kan inte ångras.'
        }
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={deleteType === 'room' ? confirmDeleteRoom : confirmDeleteItem}
        onCancel={() => setDeleteConfirmId(null)}
      />
    </PageShell>
  );
};

export default RoomListPage;
